/**
 * Print asset helpers.
 */

/**
 * The image URL to show for a print asset in selection UIs: the lightweight
 * preview when available, the full asset image otherwise. Card element
 * configurations always reference the full asset image directly — never use
 * this for the card canvas itself.
 *
 * @param {?Object} printAsset The print asset.
 * @returns {?string}
 */
export const getPrintAssetDisplayImageUrl = printAsset =>
  printAsset?.preview_asset_image?.url ?? printAsset?.asset_image?.url ?? null;

// SYSTEM print asset names carry their orientation by convention:
// "Smoke — Red (Landscape)".
const PRINT_ASSET_ORIENTATION_SUFFIX = /\s*\((Portrait|Landscape)\)\s*$/;

/**
 * The orientation encoded in a print asset's name.
 *
 * @param {?Object} printAsset The print asset.
 * @returns {?string} An ORIENTATION value, or null when not encoded.
 */
export const getPrintAssetOrientation = printAsset => {
  const match = (printAsset?.name ?? '').match(PRINT_ASSET_ORIENTATION_SUFFIX);

  return match ? match[1].toUpperCase() : null;
};

/**
 * Narrows print assets to the ones available to an identity's jersey colors.
 *
 * An untagged asset is universal; a tagged one has to intersect the identity's
 * colors. Colors narrow the choice but never remove it — when nothing matches
 * (an event whose assets are all tagged for other colors) the whole set stands,
 * rather than leaving the buyer with nothing to pick. No identity colors is not
 * a filter, so everything stands there too.
 *
 * @param {Array} printAssets The print assets.
 * @param {Array} jerseyColors The identity's JerseyColorEnum values.
 * @returns {Array}
 */
export const filterPrintAssetsByJerseyColors = (
  printAssets = [],
  jerseyColors = [],
) => {
  if (jerseyColors.length === 0) {
    return printAssets;
  }

  const matching = printAssets.filter(
    ({ jersey_colors: assetColors }) =>
      !assetColors?.records?.length ||
      !!assetColors?.records?.find(({ jersey_color: color }) =>
        jerseyColors.includes(color),
      ),
  );

  return matching.length > 0 ? matching : printAssets;
};
